<template>
    <div>
        <div class="flex flex-row" v-if="!object">
            <div v-if="creatable" class="relative inline-block">
                <button
                    :disabled="disabled"
                    class="border-0 bg-0 flex flex-row items-center"
                    :class="
                        disabled
                            ? 'text-gray-600 dark:text-gray-300 cursor-not-allowed'
                            : 'text-blue-600 dark:text-blue-300 hover:text-gray-600 dark:hover:text-gray-300'
                    "
                    type="button"
                    @click="openNew"
                    v-if="creatable"
                >
                    +  {{createText}}
                    <span class="text-gray-400 ml-3" v-if="!modelData">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="3"
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    </span>
                </button>
                <transition name="dropdown">
                    <div
                        v-show="newOpen"
                        class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg z-10"
                    >
                        <div
                            class="rounded-md bg-white dark:bg-black ring-1 ring-black dark:ring-white ring-opacity-5"
                            role="menu"
                            aria-orientation="vertical"
                            aria-labelledby="options-menu"
                        >
                            <div class="py-1">
                                <a
                                    v-for="type in Object.keys(contentTypes)"
                                    :key="`contentType-${type}`"
                                    @click="() => openSlide(type, 'Form')"
                                    class="cursor-pointer block px-4 py-2 text-sm leading-5 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-gray-100 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-800 focus:text-gray-900 dark:focus:text-gray-50"
                                    role="menuitem"
                                >
                                    {{ type }}
                                </a>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>
            <div
                :class="['relative', 'inline-block', creatable ? 'ml-5' : null]"
                v-if="linkable"
            >
                <button
                    :disabled="disabled"
                    class="border-0 bg-0 flex flex-row items-center"
                    :class="
                        disabled
                            ? 'text-gray-600 dark:text-gray-300 cursor-not-allowed'
                            : 'text-blue-600 dark:text-blue-300 hover:text-gray-600 dark:hover:text-gray-300'
                    "
                    type="button"
                    @click="openLink"
                    :dusk="`${componentName}_link_btn`"
                    v-if="linkable"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                        />
                    </svg>
                    <span class="inline-block ml-1"
                        >{{linkText}}</span
                    >
                    <span class="text-gray-400 ml-3" v-if="!modelData">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="3"
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    </span>
                </button>
                <transition name="dropdown">
                    <div
                        v-show="linkOpen"
                        class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg z-10"
                    >
                        <div
                            class="rounded-md bg-white ring-1 ring-black ring-opacity-5"
                            role="menu"
                            aria-orientation="vertical"
                            aria-labelledby="options-menu"
                        >
                            <div class="py-1">
                                <a
                                    v-for="type in Object.keys(contentTypes)"
                                    :key="`contentTypeLink-${type}`"
                                    @click="() => openSlide(type, 'List')"
                                    class="cursor-pointer block px-4 py-2 text-sm leading-5 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-gray-100 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-800 focus:text-gray-900 dark:focus:text-gray-50"
                                    role="menuitem"
                                >
                                    {{ type }}
                                </a>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>

            <div
                v-if="!linkable && !editable && !creatable && !deletable"
                :class="{ 'opacity-50': !deletable }"
            >
                None selected
            </div>
        </div>
        <div v-if="object">
            <div
                class="flex flex-row items-center"
                :class="{ 'opacity-50': !deletable }"
            >
                <p class="text-gray-500 dark:text-gray-300">
                    Referenced:
                    <span class="text-gray-700 dark:text-gray-200">
                        {{ getItemLabel(object) }}
                    </span>

                    <span v-if="debugStore.debugMode && object.id" class="font-mono">
                        [{{ model }}:{{ object.id }}]
                    </span>
                </p>
                <span
                    @click="
                        () => {
                            if (this.disabled) return;
                            this.object = null;
                        }
                    "
                    v-if="deletable"
                    :class="
                        disabled
                            ? 'text-gray-600 dark:text-gray-300 cursor-not-allowed'
                            : 'text-blue-600 dark:text-blue-300 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer'
                    "
                    class="ml-4 transition duration-150 transform hover:scale-105"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </span>
                <span
                    @click="
                        () => {
                            if (this.disabled) return;
                        }
                    "
                    v-if="shouldOpenNewWindow"
                    :class="
                        disabled
                            ? 'text-gray-600 dark:text-gray-300 cursor-not-allowed'
                            : 'text-blue-600 dark:text-blue-300 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer'
                    "
                    class="ml-1 transition duration-150 transform hover:scale-105"
                >
                    <a :href="this.generateObjectEditRoute(object).edit_route" target="_blank">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                        </svg>
                    </a>
                </span>
                <span
                    @click="
                        () => {
                            if (this.disabled) return;
                            this.openSlide(this.model, 'Form');
                        }
                    "
                    :class="
                        disabled
                            ? 'text-gray-600 dark:text-gray-300 cursor-not-allowed'
                            : 'text-blue-600 dark:text-blue-300 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer'
                    "
                    class="ml-1 transition duration-150 transform hover:scale-105"
                    v-if="editable"
                >
                    <svg
                        class="h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                    </svg>
                </span>
            </div>
        </div>
        <input
            v-if="object"
            type="hidden"
            :name="inputName"
            :value="object.id"
        />
        <slide-over
            :empty-message="emptyMessage"
            @close="slideOpen = false"
            :is-open="slideOpen"
            :component="component"
            :formName="baseFormName"
            :object="this.object"
            :model="model"
            :language="language"
            :excluded-ids="excludedIds"
            :routeKeyName="model ? contentTypes[model].routeKeyName : 'id'"
            :with-docs="withDocs"
            :label="
                model
                    ? contentTypes[model]
                        ? contentTypes[model].columnSelector
                        : null
                    : null
            "
            :formRoute="formRoute"
            :listRoute="
                model
                    ? contentTypes[model]
                        ? contentTypes[model].listRoute
                        : null
                    : null
            "
            @created="objectCreated"
            @selected="objectCreated"
            :query="query"
            :order="order"
            :pre-filled="preFilled"
        />
        <div v-if="error" class="mt-3">
            <error :error="error" />
        </div>
    </div>
</template>
<script>
import reference from "../mixins/reference";
import siteLanguage from '../config/siteLanguage'
import {useDebugStore} from "../stores/debug";
export default {
    name: "reference",
    mixins: [reference],
    props: {
        fieldName: String,
        value: Object,
        modelData: String,
        language: {
            type: Object,
            default: () => {
                return { id: siteLanguage.language.ENGLISH, iso: siteLanguage.iso.ENGLISH };
            },
        },
        twoStepCreate: Boolean,
        editable: {
            type: Boolean,
            default: false,
            required: false,
        },
        deletable: {
            type: Boolean,
            default: true,
            required: false,
        },
        creatable: {
            type: Boolean,
            default: true,
            required: false,
        },
        linkable: {
            type: Boolean,
            default: true,
            required: false,
        },
        inline: {
            type: Boolean,
            default: false,
            required: false,
        },
        error: {
            type: String,
            required: false,
        },
        excludedIds: Array,
        query: Object,
        order: Object,
        preFilled: {
            type: Object,
            required: false,
        },
        createShouldHaveConfirmation: {
            type: Function,
            required: false,
        },
        confirmationMessage: {
            type: String,
            required: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        withDocs: {
            type: Array,
            required: false,
        },
        emptyMessage: {
            type: String,
            required: false,
        },
        createText: {
            type: String,
            required: false,
            default: 'Create and link a new entry'
        },
        linkText: {
            type: String,
            required: false,
            default: 'Link an existing entry'
        },
        shouldOpenNewWindow: {
            type: Boolean,
            required: false,
            default: false
        },
    },
    data() {
        return {
            newOpen: false,
            linkOpen: false,
            slideOpen: false,
            model: this.modelData || null,
            object: this.value || null,
            component: null,
            debugStore: useDebugStore(),
        };
    },
    created() {
        if (this.object) {
            this.object = this.generateObjectUpdateRoute(this.object);
        }
    },
    methods: {
        openSlide(model, type) {
            this.model = model;
            this.component = type === "Form" ? this.formName : "ModelList";
            this.slideOpen = true;
            this.newOpen = false;
            this.linkOpen = false;
        },
        openLink() {
            if (this.disabled) return;
            if (this.modelData) {
                this.openSlide(this.model, "Link");
                return;
            }
            this.linkOpen = !this.linkOpen;
            this.newOpen = false;
        },
        openNew() {
            if (this.disabled) return;
            if (this.modelData) {
                this.openSlide(this.model, "Form");
                return;
            }
            this.newOpen = !this.newOpen;
            this.linkOpen = false;
        },
        objectCreated({ object }) {
            object = this.generateObjectUpdateRoute(object);
            this.handleCreate(object);
        },
        handleCreate(object) {
            if (
                !this.createShouldHaveConfirmation ||
                (this.createShouldHaveConfirmation &&
                    !this.createShouldHaveConfirmation(object))
            ) {
                this.object = object;
                this.slideOpen = false;
            } else {
                this.$confirm({
                    message: this.confirmationMessage ?? `Are you sure?`,
                    button: {
                        no: "No",
                        yes: "Yes",
                    },
                    callback: (confirm) => {
                        if (confirm) {
                            this.object = object;
                            this.slideOpen = false;
                        }
                    },
                });
            }
        }
    },
    computed: {
        formName() {
            return this.twoStepCreate && this.language.id !== 1 && !this.object
                ? `two-step-create`
                : `${this.componentName}-form`;
        },
        baseFormName() {
            return `${this.componentName}-form`;
        },
        componentName() {
            return this.model
                ? this.model.toLowerCase().split(" ").join("-")
                : "";
        },
        inputName() {
            return this.fieldName || `${this.model.toLowerCase()}_id`;
        },
        formRoute() {
            if (this.object) {
                return this.object.updateRoute;
            }
            if (this.model) {
                return this.contentTypes[this.model]
                    ? this.contentTypes[this.model].formRoute
                    : null;
            }
        },
    },
    watch: {
        object(newValue) {
            this.$emit("input", newValue);
        },
        value(newValue) {
            this.object = newValue;
        },
        modelData(newValue) {
            this.model = newValue;
        },
    },
};
</script>
